import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button,
  Progress,
  Spin,
  Modal,
  Tag,
  Empty,
  notification
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  LeftOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  LockOutlined
} from '@ant-design/icons';
import {
  getQuizByContentId,
  submitQuizAttempt,
  getCourseById
} from '../../../../../Constants/Api/functions/function';
import { BASE_URL } from '../../../../../Constants/Api/azure/azure';
import './style.scss';

const formatImgUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const origin = BASE_URL.replace(/\/api\/?$/, '');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
};

const ContentQuiz = ({
  modalMode = false,
  propCourseId,
  propContentId,
  propCourse,
  onClose,
  onQuizCompleted,
  onQuizStateChange
}) => {
  const params = useParams() || {};
  const history = useHistory();
  const userProfile = useSelector((state) => state.userProfile.userProfile);

  const courseId = propCourseId || params.id;
  const contentId = propContentId || params.contentId;

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [course, setCourse] = useState(propCourse || null);
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attempt, setAttempt] = useState(null);

  // Runner state
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({}); // { [questionId]: [0, 2] }
  const [api, contextHolder] = notification.useNotification();

  // Timer states
  const [remainingTotalSeconds, setRemainingTotalSeconds] = useState(0);
  const [remainingQuestionSeconds, setRemainingQuestionSeconds] = useState(0);

  // Refs for timer callbacks to avoid stale closures
  const userAnswersRef = useRef(userAnswers);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    userAnswersRef.current = userAnswers;
  }, [userAnswers]);

  // Warn if user attempts to close/reload page while quiz is in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isQuizStarted && !attempt) {
        e.preventDefault();
        e.returnValue = 'Quiz is currently in progress. If you leave now, your answers may be lost.';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isQuizStarted, attempt]);

  useEffect(() => {
    loadCourseAndQuiz();
  }, [courseId, contentId, userProfile]);

  const loadCourseAndQuiz = async () => {
    setIsLoading(true);
    try {
      // 1. Load course details if not provided
      if (!course && courseId) {
        const courseRes = await getCourseById(courseId);
        if (courseRes) {
          setCourse(Array.isArray(courseRes) ? courseRes[0] : courseRes);
        }
      }

      // 2. Load quiz data
      if (contentId) {
        const quizRes = await getQuizByContentId(
          contentId,
          userProfile?.id,
          userProfile?.role
        );
        if (quizRes && quizRes.success) {
          setQuiz(quizRes.quiz);
          setQuestions(quizRes.questions || []);
          setAttempt(quizRes.attempt || null);
          if (!quizRes.attempt) {
            setIsQuizStarted(false);
            if (onQuizStateChange) onQuizStateChange(false);
            setCurrentIndex(0);
            setUserAnswers({});
          }
        }
      }
    } catch (error) {
      console.error('Error loading quiz page:', error);
    }
    setIsLoading(false);
  };

  const getSecondsPerQuestion = () => {
    if (!quiz?.timeLimitMinutes || Number(quiz.timeLimitMinutes) <= 0) {
      return Number(quiz?.timeLimitSeconds) || 0;
    }
    const totalSecs = Number(quiz.timeLimitMinutes) * 60;
    const qCount = questions.length || 1;
    return Math.floor(totalSecs / qCount);
  };

  const handleStartQuiz = () => {
    setIsQuizStarted(true);
    setCurrentIndex(0);
    setUserAnswers({});
    isSubmittingRef.current = false;
    if (onQuizStateChange) onQuizStateChange(true);

    const totalSecs = (quiz?.timeLimitMinutes && Number(quiz.timeLimitMinutes) > 0)
      ? Number(quiz.timeLimitMinutes) * 60
      : 0;
    setRemainingTotalSeconds(totalSecs);

    const qSecs = getSecondsPerQuestion();
    setRemainingQuestionSeconds(qSecs);
  };

  // Reset per-question timer when question changes
  useEffect(() => {
    if (isQuizStarted && !attempt) {
      const qSecs = getSecondsPerQuestion();
      if (qSecs > 0) {
        setRemainingQuestionSeconds(qSecs);
      }
    }
  }, [currentIndex, isQuizStarted, attempt, quiz?.timeLimitMinutes, questions.length]);

  // Overall Quiz Timer Countdown
  useEffect(() => {
    if (!isQuizStarted || attempt || !quiz?.timeLimitMinutes || Number(quiz.timeLimitMinutes) <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRemainingTotalSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          triggerAutoSubmit('Total quiz time has expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuizStarted, attempt, quiz?.timeLimitMinutes]);

  // Per-Question Timer Countdown (Auto Next / Auto Submit)
  useEffect(() => {
    const qSecs = getSecondsPerQuestion();
    if (!isQuizStarted || attempt || qSecs <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRemainingQuestionSeconds((prev) => {
        if (prev <= 1) {
          if (currentIndex < questions.length - 1) {
            // Auto Next
            api.info({
              message: 'Question Time Expired',
              description: `Time for Question ${currentIndex + 1} has expired. Automatically moving to the next question...`,
              duration: 2
            });
            setCurrentIndex((curr) => curr + 1);
            return qSecs;
          } else {
            // Final question time expired -> Auto Submit
            clearInterval(timer);
            triggerAutoSubmit('Time has expired on the final question');
            return 0;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuizStarted, attempt, quiz?.timeLimitMinutes, currentIndex, questions.length]);

  const triggerAutoSubmit = (reason) => {
    if (isSubmittingRef.current) return;
    api.warning({
      message: 'Time Expired',
      description: `${reason}. Submitting your quiz answers automatically...`,
      duration: 3
    });
    performSubmit(userAnswersRef.current);
  };

  const handleSelectOption = (questionId, optIndex, questionType) => {
    setUserAnswers((prev) => {
      const currentSelected = prev[questionId] || [];
      if (questionType === 'multiple') {
        if (currentSelected.includes(optIndex)) {
          return {
            ...prev,
            [questionId]: currentSelected.filter((i) => i !== optIndex)
          };
        } else {
          return {
            ...prev,
            [questionId]: [...currentSelected, optIndex]
          };
        }
      } else {
        // Single choice
        return {
          ...prev,
          [questionId]: [optIndex]
        };
      }
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleSubmitConfirm = () => {
    const answeredCount = Object.keys(userAnswers).filter(
      (k) => userAnswers[k] && userAnswers[k].length > 0
    ).length;

    const unAnswered = questions.length - answeredCount;

    Modal.confirm({
      title: 'Submit Quiz Confirmation',
      icon: <QuestionCircleOutlined style={{ color: '#6366f1' }} />,
      content: (
        <div>
          <p>
            You have answered <strong>{answeredCount}</strong> of <strong>{questions.length}</strong> questions.
          </p>
          {unAnswered > 0 && (
            <p style={{ color: '#d97706', fontWeight: 600 }}>
              ⚠️ You have {unAnswered} unanswered question(s). Unanswered questions will receive 0 marks.
            </p>
          )}
          <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
            Note: Only <strong>1 attempt</strong> is permitted for each content quiz. Once submitted, your score will be recorded in your course progress.
          </p>
        </div>
      ),
      okText: 'Submit Quiz Now',
      okType: 'primary',
      cancelText: 'Continue Answering',
      onOk: () => performSubmit(userAnswersRef.current)
    });
  };

  const performSubmit = async (customAnswers) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const payload = {
        quizId: quiz.id,
        courseContentId: contentId,
        courseId: courseId,
        userId: userProfile?.id,
        answers: customAnswers || userAnswersRef.current || userAnswers
      };

      const res = await submitQuizAttempt(payload);
      if (res && res.success) {
        api.success({
          message: 'Quiz Completed!',
          description: `Your score: ${res.attempt.score} / ${res.attempt.totalMarks} (${res.attempt.percentage}%)`
        });
        setAttempt(res.attempt);
        setIsQuizStarted(false);
        if (onQuizStateChange) onQuizStateChange(false);
        // Notify parent if modal
        if (onQuizCompleted) {
          onQuizCompleted(res.attempt);
        }
        // Reload to update correct answers in review
        await loadCourseAndQuiz();
      } else {
        api.error({
          message: 'Submission Failed',
          description: res.message || 'Could not submit quiz attempt'
        });
        isSubmittingRef.current = false;
      }
    } catch (err) {
      console.error('Quiz submit error:', err);
      api.error({
        message: 'Submission Error',
        description: 'An unexpected error occurred while submitting.'
      });
      isSubmittingRef.current = false;
    }
    setIsSubmitting(false);
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return '#16a34a';
      case 'B':
        return '#2563eb';
      case 'C':
        return '#d97706';
      case 'S':
        return '#0284c7';
      default:
        return '#dc2626';
    }
  };

  const handleExitQuiz = () => {
    if (isQuizStarted && !attempt) {
      api.warning({
        message: 'Quiz in Progress',
        description: 'You cannot exit while the quiz is active. Please finish answering and submit your quiz.'
      });
      return;
    }
    if (modalMode && onClose) {
      onClose();
    } else {
      history.push(`/dashboard/cources/${courseId}`);
    }
  };

  if (isLoading) {
    return (
      <div className={`content-quiz-page ${modalMode ? 'in-modal' : ''}`} style={{ textAlign: 'center', paddingTop: 80, paddingBottom: 80 }}>
        <Spin size="large" tip="Loading Quiz..." />
      </div>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <div className={`content-quiz-page ${modalMode ? 'in-modal' : ''}`}>
        {!modalMode && (
          <div className="quiz-page-header">
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              className="back-btn"
              onClick={handleExitQuiz}
            >
              Back to Course
            </Button>
          </div>
        )}
        <div className="quiz-intro-card">
          <Empty
            description={
              <div>
                <h2>No Quiz Available</h2>
                <p>There is currently no quiz configured for this lesson.</p>
              </div>
            }
          >
            <Button
              type="primary"
              onClick={handleExitQuiz}
            >
              {modalMode ? 'Close' : 'Return to Course'}
            </Button>
          </Empty>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const currentSelectedOptions = currentQuestion ? userAnswers[currentQuestion.id] || [] : [];
  const progressPercent = parseFloat((((currentIndex + 1) / questions.length) * 100).toFixed(0));

  // Extract active options for current question
  const currentOptions = (currentQuestion?.options && currentQuestion.options.length > 0)
    ? currentQuestion.options
    : [
        currentQuestion?.option1,
        currentQuestion?.option2,
        currentQuestion?.option3,
        currentQuestion?.option4,
        currentQuestion?.option5
      ].filter(opt => opt !== null && opt !== undefined && opt !== '');

  return (
    <div className={`content-quiz-page ${modalMode ? 'in-modal' : ''}`}>
      {contextHolder}

      {/* Header Bar */}
      <div className="quiz-page-header">
        {!modalMode && !isQuizStarted && (
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            className="back-btn"
            onClick={handleExitQuiz}
          >
            Back to Course
          </Button>
        )}

        {isQuizStarted && !attempt ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                background: '#fee2e2',
                color: '#b91c1c',
                border: '1px solid #fca5a5',
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: '0.82rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <LockOutlined />
              Quiz in Progress • Close Disabled
            </span>
          </div>
        ) : modalMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.2rem' }}>📝</span>
            <span style={{ fontWeight: 700, color: '#334155', fontSize: '1.1rem' }}>
              Lesson Quiz
            </span>
          </div>
        ) : null}

        <div className="course-breadcrumb" style={{ textAlign: modalMode ? 'right' : 'left' }}>
          {course?.name && <span className="course-name">{course.name}</span>}
          <h1 className="content-title" style={{ fontSize: modalMode ? '1.15rem' : '1.35rem' }}>
            {quiz?.title || 'Course Content Quiz'}
          </h1>
        </div>
      </div>

      {/* STATE 1: ALREADY COMPLETED (Score Screen & Review) */}
      {attempt ? (
        <div className="quiz-result-card">
          <div className="result-hero">
            <span className="trophy-icon">🏆</span>
            <h2>Quiz Results & Score</h2>
            <p className="result-subtitle">
              Submitted on {new Date(attempt.submittedAt).toLocaleDateString()} at{' '}
              {new Date(attempt.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>

            <div className="score-metrics">
              <div className="metric-card">
                <span className="label">Score Obtained</span>
                <span className="value">
                  {parseFloat(attempt.score).toFixed(1)} / {parseFloat(attempt.totalMarks).toFixed(1)}
                </span>
              </div>

              <div className="metric-card">
                <span className="label">Percentage</span>
                <span className="value">{attempt.percentage}%</span>
              </div>

              <div className="metric-card highlight-grade">
                <span className="label">Calculated Grade</span>
                <span className="value" style={{ color: getGradeColor(attempt.grade) }}>
                  {attempt.grade || 'Pass'}
                </span>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <Tag color="purple" style={{ fontSize: '0.9rem', padding: '6px 14px', borderRadius: 8 }}>
                ✓ Single attempt completed. Your grade has been recorded in your course progress.
              </Tag>
            </div>
          </div>

          {/* Question Review Section */}
          <div className="review-section">
            <h3>
              <QuestionCircleOutlined /> Review Your Answers
            </h3>

            <div className="review-questions-list">
              {questions.map((q, qIdx) => {
                const studentAns = (attempt.answers && attempt.answers[q.id]) || [];
                const correctAns = Array.isArray(q.correctAnswers) ? q.correctAnswers : [];
                const qMarks = parseFloat(q.marks) || 1;
                const isMultiple = q.questionType === 'multiple' || correctAns.length > 1;

                // Non-empty options for review
                const qOptions = (q.options && q.options.length > 0)
                  ? q.options
                  : [q.option1, q.option2, q.option3, q.option4, q.option5].filter(
                      opt => opt !== null && opt !== undefined && opt !== ''
                    );

                let earnedMarks = 0;
                let isExactMatch = false;

                if (isMultiple) {
                  if (correctAns.length > 0 && studentAns.length > 0) {
                    const markPerCorrect = qMarks / correctAns.length;
                    const correctCount = studentAns.filter((c) => correctAns.includes(c)).length;

                    if (studentAns.length >= 5 && correctAns.length < 5) {
                      earnedMarks = 0;
                    } else {
                      earnedMarks = Math.min(qMarks, parseFloat((correctCount * markPerCorrect).toFixed(2)));
                    }

                    isExactMatch = correctCount === correctAns.length && studentAns.length === correctAns.length;
                  }
                } else {
                  isExactMatch =
                    correctAns.length > 0 &&
                    studentAns.length === 1 &&
                    correctAns.includes(studentAns[0]);
                  earnedMarks = isExactMatch ? qMarks : 0;
                }

                const isPartial = !isExactMatch && earnedMarks > 0;

                return (
                  <div
                    key={q.id || qIdx}
                    className={`review-q-card ${isExactMatch ? 'passed' : isPartial ? 'partial' : 'failed'}`}
                  >
                    <div className="q-header-row">
                      <div className="q-title-wrap">
                        <span>Q{qIdx + 1}. </span>
                        <span>{q.questionText}</span>
                      </div>
                      <div className="q-status-badge">
                        {isExactMatch ? (
                          <Tag color="success" icon={<CheckCircleOutlined />}>
                            Correct (+{earnedMarks} Marks)
                          </Tag>
                        ) : isPartial ? (
                          <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                            Partial Credit (+{earnedMarks}/{qMarks} Marks)
                          </Tag>
                        ) : (
                          <Tag color="error" icon={<CloseCircleOutlined />}>
                            Incorrect (0/{qMarks} Marks)
                          </Tag>
                        )}
                      </div>
                    </div>

                    {/* Question Image in Review */}
                    {q.image && (
                      <div className="review-q-image" style={{ margin: '10px 0 16px' }}>
                        <img
                          src={formatImgUrl(q.image)}
                          alt="Question Diagram"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 220,
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    )}

                    <div className="review-options">
                      {qOptions.map((optText, optIdx) => {
                        const isStudentChoice = studentAns.includes(optIdx);
                        const isCorrectChoice = correctAns.includes(optIdx);

                        let optClass = '';
                        if (isCorrectChoice) optClass = 'correct-answer';
                        else if (isStudentChoice && !isCorrectChoice) optClass = 'wrong-selected';

                        return (
                          <div key={optIdx} className={`review-opt-row ${optClass}`}>
                            <span className="opt-badge">{String.fromCharCode(65 + optIdx)}</span>
                            <span className="opt-text">{optText}</span>

                            {isCorrectChoice && isStudentChoice && (
                              <span className="tag-status" style={{ background: '#22c55e', color: '#fff' }}>
                                ✓ Your Correct Answer
                              </span>
                            )}
                            {isCorrectChoice && !isStudentChoice && (
                              <span className="tag-status" style={{ background: '#10b981', color: '#fff' }}>
                                Correct Answer
                              </span>
                            )}
                            {!isCorrectChoice && isStudentChoice && (
                              <span className="tag-status" style={{ background: '#ef4444', color: '#fff' }}>
                                ✗ Your Incorrect Choice
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {q.explanation && (
                      <div className="explanation-callout">
                        <strong>Explanation: </strong> {q.explanation}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Button
                type="primary"
                size="large"
                shape="round"
                onClick={handleExitQuiz}
              >
                {modalMode ? 'Close Quiz' : 'Return to Course Lessons'}
              </Button>
            </div>
          </div>
        </div>
      ) : !isQuizStarted ? (
        /* STATE 2: INTRO CARD BEFORE START */
        <div className="quiz-intro-card">
          <span className="intro-icon">📝</span>
          <h1>{quiz.title || 'Lesson Quiz'}</h1>
          <p className="intro-desc">
            Test your knowledge of this course content. Please read each question carefully.
            Questions will be displayed <strong>one by one</strong>.
          </p>

          <div className="quiz-rules-grid">
            <div className="rule-item">
              <span className="rule-icon">❓</span>
              <div className="rule-info">
                <span className="title">Questions</span>
                <span className="val">{questions.length} Items</span>
              </div>
            </div>

            <div className="rule-item">
              <span className="rule-icon">⭐</span>
              <div className="rule-info">
                <span className="title">Total Marks</span>
                <span className="val">{quiz.totalMarks || questions.length} Points</span>
              </div>
            </div>

            <div className="rule-item">
              <span className="rule-icon">🔒</span>
              <div className="rule-info">
                <span className="title">Attempt Limit</span>
                <span className="val">1 Attempt Only</span>
              </div>
            </div>

            <div className="rule-item">
              <span className="rule-icon">🎯</span>
              <div className="rule-info">
                <span className="title">Passing Score</span>
                <span className="val">{quiz.passingPercentage || 50}%</span>
              </div>
            </div>

            {Number(quiz.timeLimitMinutes) > 0 && (
              <>
                <div className="rule-item highlight-timer">
                  <span className="rule-icon">⏱️</span>
                  <div className="rule-info">
                    <span className="title">Quiz Time Limit</span>
                    <span className="val">{quiz.timeLimitMinutes} Minutes Total</span>
                  </div>
                </div>

                <div className="rule-item highlight-timer">
                  <span className="rule-icon">⏳</span>
                  <div className="rule-info">
                    <span className="title">Time Per Question</span>
                    <span className="val">{getSecondsPerQuestion()}s / Question (Auto-Next)</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <Button
            type="primary"
            size="large"
            className="start-quiz-btn"
            onClick={handleStartQuiz}
          >
            Start Quiz Now
          </Button>
        </div>
      ) : (
        /* STATE 3: ONE-BY-ONE QUESTION TAKER RUNNER */
        <div className="quiz-runner-card">
          <div className="runner-top-bar">
            <div className="runner-progress-header">
              <div className="q-counter">
                <span>Question</span>
                <span className="badge-pill">
                  {currentIndex + 1} of {questions.length}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* Per-Question Countdown Timer (Divided from Total Quiz Time) */}
                {getSecondsPerQuestion() > 0 && (
                  <div
                    className={`question-timer-badge ${remainingQuestionSeconds <= 10 ? 'urgent-pulse' : ''}`}
                    style={{
                      background: remainingQuestionSeconds <= 10 ? '#ffedd5' : '#f0fdf4',
                      color: remainingQuestionSeconds <= 10 ? '#c2410c' : '#15803d',
                      border: remainingQuestionSeconds <= 10 ? '1px solid #fb923c' : '1px solid #86efac',
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontWeight: 700,
                      fontSize: '0.88rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <span>⏳ {remainingQuestionSeconds}s</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 500, opacity: 0.85 }}>This Question</span>
                  </div>
                )}

                {/* Total Quiz Countdown Timer */}
                {Number(quiz?.timeLimitMinutes) > 0 && (
                  <div
                    className={`quiz-timer-badge ${remainingTotalSeconds <= 60 ? 'urgent-pulse' : ''}`}
                    style={{
                      background: remainingTotalSeconds <= 60 ? '#fee2e2' : '#eef2ff',
                      color: remainingTotalSeconds <= 60 ? '#b91c1c' : '#4338ca',
                      border: remainingTotalSeconds <= 60 ? '1px solid #f87171' : '1px solid #c7d2fe',
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontWeight: 700,
                      fontSize: '0.88rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <ClockCircleOutlined />
                    <span>{formatTime(remainingTotalSeconds)}</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 500, opacity: 0.85 }}>Total</span>
                  </div>
                )}

                <div className="q-marks-pill">
                  ⭐ {currentQuestion?.marks} {Number(currentQuestion?.marks) === 1 ? 'Mark' : 'Marks'}
                </div>
              </div>
            </div>
            <Progress
              percent={progressPercent}
              showInfo={false}
              strokeColor={{ from: '#4f46e5', to: '#7c3aed' }}
            />
          </div>

          <div className="question-body">
            <h2 className="question-prompt">{currentQuestion?.questionText}</h2>

            {/* Question Image */}
            {currentQuestion?.image && (
              <div className="runner-question-image" style={{ textAlign: 'center', margin: '14px 0 20px' }}>
                <img
                  src={formatImgUrl(currentQuestion.image)}
                  alt="Question Diagram"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 260,
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}

            <div className="instruction-hint">
              {currentQuestion?.questionType === 'multiple'
                ? 'ℹ️ Multiple Choice: Select all answers that are correct.'
                : 'ℹ️ Single Choice: Select the one correct answer.'}
            </div>

            <div className="answers-container">
              {currentOptions.map((optText, optIdx) => {
                const isSelected = currentSelectedOptions.includes(optIdx);
                const letter = String.fromCharCode(65 + optIdx);

                return (
                  <div
                    key={optIdx}
                    className={`answer-choice-btn ${isSelected ? 'selected' : ''}`}
                    onClick={() =>
                      handleSelectOption(
                        currentQuestion.id,
                        optIdx,
                        currentQuestion.questionType
                      )
                    }
                  >
                    <div className="opt-letter">{letter}</div>
                    <div className="opt-text">{optText}</div>
                    <div className="select-indicator">
                      {isSelected ? (
                        <CheckCircleOutlined style={{ color: '#4f46e5', fontSize: '1.2rem' }} />
                      ) : (
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: currentQuestion.questionType === 'multiple' ? 4 : '50%',
                            border: '2px solid #cbd5e1'
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="runner-footer">
            <Button
              icon={<LeftOutlined />}
              disabled={currentIndex === 0}
              onClick={handlePrev}
            >
              Previous
            </Button>

            {!isLastQuestion ? (
              <Button type="primary" onClick={handleNext}>
                Next Question <RightOutlined />
              </Button>
            ) : (
              <Button
                type="primary"
                loading={isSubmitting}
                onClick={handleSubmitConfirm}
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  borderColor: '#059669',
                  fontWeight: 600
                }}
              >
                Submit Quiz
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentQuiz;
